import React from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Grid, Paper } from '@mui/material'
import { Skeleton, Typography } from '@mui/material'
import Joi from 'joi'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { CardTitle, ControlledTextField } from 'components/common'
import {
  DynamicEmailTemplateType,
  GetDynamicEmailTemplatesDocument,
  GetDynamicEmailTemplatesQuery,
  useGetDynamicEmailTemplatesQuery,
  useUpsertDynamicEmailTemplateMutation,
} from 'generated/graphql'

const templateFormSchema = Joi.object({
  templateId: Joi.string().required().max(100).label('Template ID'),
})

interface IProps {
  type: DynamicEmailTemplateType
  label: string
}

interface IFormData {
  templateId: string
}

export const EmailTemplateMapping = (props: IProps) => {
  const { type, label } = props
  const [template, setTemplate] =
    React.useState<GetDynamicEmailTemplatesQuery['dynamicEmailTemplates'][0]>()

  const methods = useForm<IFormData>({
    shouldUnregister: true,
    resolver: joiResolver(templateFormSchema),
    defaultValues: {
      templateId: '',
    },
  })

  const {
    reset,
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = methods

  const { loading: templateLoading } = useGetDynamicEmailTemplatesQuery({
    variables: {
      where: { emailType: { eq: type } },
    },
    onCompleted: (response) => {
      setTemplate(response.dynamicEmailTemplates[0])
    },
  })

  const [upsertTemplate, { loading: savingTemplate }] = useUpsertDynamicEmailTemplateMutation({
    onCompleted: () => {
      toast.success('Email template mapping saved successfully')
    },
    refetchQueries: [
      {
        query: GetDynamicEmailTemplatesDocument,
        variables: { where: { emailType: { eq: type } } },
      },
    ],
  })


  React.useEffect(() => {
    reset({
      templateId: template?.templateId || '',
    })
  }, [reset, template])

  const onSubmit = (formData: IFormData) => {
    upsertTemplate({
      variables: {
        emailType: type,
        templateId: formData.templateId,
        description: null,
      },
    })
  }

  const handleCancel = () => {
    reset({
      templateId: template?.templateId || '',
    })
  }

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper variant='outlined' sx={{ padding: 2 }}>
              <CardTitle>{`${label} Template Mapping`}</CardTitle>
              {templateLoading ? (
                <>
                  <Typography variant='h2'>
                    <Skeleton animation='wave' />
                    <Skeleton animation='wave' />
                  </Typography>
                </>
              ) : (
                <Box py={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control}
                        name='templateId'
                        label='SendGrid Template ID'
                        required
                        error={!!errors.templateId}
                        helperText={errors.templateId?.message}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item container justifyContent='flex-end' spacing={2}>
            <Grid item>
              <Button variant='outlined' onClick={handleCancel} disabled={!isDirty}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <LoadingButton
                variant='contained'
                type='submit'
                loading={savingTemplate}
                disabled={!isDirty}>
                Save
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
